/**
 * Site header — sticky top bar with the tenant logo, primary nav,
 * and user cluster. Per ui-guidelines.md §5.1.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import type { TenantBranding } from "@starz/shared/api/types.gen";
import { useAuthUser } from "@/lib/auth/useAuth";
import { doSignOut } from "@/lib/auth/useAuth";

interface BrandHeaderProps {
  branding: TenantBranding | null;
}

const ADMIN_MENU_ITEMS: { to: string; label: string }[] = [
  { to: "/admin/queue", label: "Requests" },
  { to: "/admin/catalog", label: "Materials" },
  { to: "/admin/packages", label: "Packages" },
  { to: "/admin/users", label: "Users" },
];

/** Admin-only hamburger: groups the admin surfaces under one menu.
 * Closes on outside click, Escape, or item select. */
function AdminMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Admin menu"
        className="flex h-9 w-9 items-center justify-center rounded-md border border-starz-gold/30 bg-white text-starz-navy hover:bg-starz-cream"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-lg border border-starz-gold/20 bg-white py-1 shadow-lg"
        >
          <p className="px-3 py-1 text-xs font-semibold uppercase tracking-widest text-warm-gray">
            Admin
          </p>
          {ADMIN_MENU_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm font-medium text-starz-navy hover:bg-starz-cream"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BrandHeader({ branding }: BrandHeaderProps) {
  const user = useAuthUser();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-starz-gold/20 bg-starz-off-white"
    >
      <div className="mx-auto flex h-20 max-w-content items-center justify-between px-12">
        <Link to="/" aria-label={`${branding?.display_name ?? "Home"} — home`}>
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={branding.display_name} className="h-12 w-auto" />
          ) : (
            <span className="font-serif text-xl text-starz-navy">
              {branding?.display_name ?? "Remodeling Estimator"}
            </span>
          )}
        </Link>

        <div className="flex items-center gap-4">
          {user ? (
            <>
              {user.role === "tenant_admin" && <AdminMenu />}
              <span className="hidden text-sm text-warm-gray md:inline">{user.first_name}</span>
              <div
                aria-hidden="true"
                className="grid h-9 w-9 place-items-center rounded-full bg-starz-navy text-sm font-semibold text-starz-off-white"
              >
                {user.first_name.slice(0, 1).toUpperCase()}
                {user.last_name.slice(0, 1).toUpperCase()}
              </div>
              <button
                type="button"
                onClick={() => void doSignOut()}
                className="rounded-md border border-starz-gold/30 bg-white px-3 py-1.5 text-sm font-medium text-starz-navy hover:bg-starz-cream"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/signin"
              className="rounded-md bg-starz-navy px-5 py-2 text-sm font-medium text-starz-off-white hover:opacity-90"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
